import React from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { atTheBeginningOfDoc } from '@atlaskit/editor-common/selection';
import { type NextEditorPlugin } from '@atlaskit/editor-common/types';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { type EditorView } from '@atlaskit/editor-prosemirror/view';

import { encodeAdf } from '../../../common/utils/content-renderer';

export const arrowKeyPlugin: NextEditorPlugin<
	'arrowKeyPlugin',
	{
		pluginConfiguration: {
			onArrowUp: ({
				replaceDocument,
			}: {
				replaceDocument: (content: string | DocNode) => void;
			}) => void;
			onArrowDown: ({
				replaceDocument,
			}: {
				replaceDocument: (content: string | DocNode) => void;
			}) => void;
		};
	}
> = ({ config: { onArrowUp, onArrowDown }, api }) => {
	return {
		name: 'arrowKeyPlugin',
		pmPlugins() {
			return [
				{
					name: 'arrowKeyPlugin',
					plugin: () => {
						return new SafePlugin({
							props: {
								handleKeyDown: (view: EditorView, event: KeyboardEvent) => {
									if (!atTheBeginningOfDoc(view.state)) {
										return;
									}

									if (event.key === 'ArrowUp') {
										onArrowUp({
											replaceDocument: (value) => api?.core.actions.replaceDocument(value),
										});
									} else if (event.key === 'ArrowDown') {
										onArrowDown({
											replaceDocument: (value) => {
												api?.core.actions.replaceDocument(value);
											},
										});

										// Needed because the down arrow by default moves the cursor to the end of the input
										// preventDefault, stopPropagation, etc. doesn't work to prevent this in this callback
										// So we need to wait for the next tick to move the cursor to the start of the input
										new Promise((resolve) => {
											setTimeout(() => {
												view.dispatch(
													view.state.tr.setSelection(
														new TextSelection(
															view.state.tr.doc.resolve(0),
															view.state.tr.doc.resolve(0),
														),
													),
												);
												resolve(true);
											}, 0);
										});
									}
								},
							},
						});
					},
				},
			];
		},
	};
};

export type UserMessage = {
	id: string;
	content: string | DocNode;
};

export const useArrowkeyPlugin = ({ userMessages }: { userMessages: UserMessage[] }) => {
	const storeMessagesRef = React.useRef(userMessages);
	storeMessagesRef.current = userMessages;
	const currentMessageRef = React.useRef<UserMessage | undefined>(undefined);

	React.useEffect(() => {
		currentMessageRef.current = undefined;
	}, [userMessages.length]);

	const moveMessageUpOrDown = React.useCallback(
		({
			replaceDocument,
			position,
		}: {
			replaceDocument: (content: string | DocNode) => void;
			position: 'up' | 'down';
		}) => {
			const increment = position === 'up' ? -1 : 1;

			if (!storeMessagesRef.current.length) {
				return;
			}

			const currentMessageIndex = currentMessageRef.current
				? storeMessagesRef.current.findIndex(
						(message) => message.id === currentMessageRef.current?.id,
					)
				: storeMessagesRef.current.length;

			// If the currentindex is humanMessages.length, the next index will either be the last message (pressing up)
			// or out of bounds (pressing down and resetting the current message to undefined)
			const newMessageIndex = Math.max(0, currentMessageIndex + increment);

			const newMessage = storeMessagesRef.current[newMessageIndex];
			currentMessageRef.current = newMessage;
			replaceDocument(newMessage?.content ?? encodeAdf(''));
		},
		[],
	);

	const handleArrowUp = React.useCallback(
		({ replaceDocument }: { replaceDocument: (content: string | DocNode) => void }) => {
			moveMessageUpOrDown({ replaceDocument, position: 'up' });
		},
		[moveMessageUpOrDown],
	);

	const handleArrowDown = React.useCallback(
		({ replaceDocument }: { replaceDocument: (content: string | DocNode) => void }) => {
			moveMessageUpOrDown({ replaceDocument, position: 'down' });
		},
		[moveMessageUpOrDown],
	);

	return {
		handleArrowUp,
		handleArrowDown,
	};
};
